import { useFeatureFlag } from '@src/hooks/useFeatureFlag';
import type { OTCOptionFieldsData } from '@src/types/OTC';
import { isOptionProductFields } from '@src/types/OTC/otcGuards';
import { getFieldTitle, getFieldTooltip } from '@src/utils/fieldDefinitionUtils';
import {
  appendTooltipWhenFeatureDisabled,
  PHYSICAL_SETTLEMENT_SUPPORT_UPCOMING,
} from '@src/utils/OTC/otcFeatureFlagTooltipAppend';
import { otcOptionFieldDefinitions } from '@src/utils/OTC/otcOptionFieldDefinitions';
import {
  Box,
  DurationInput,
  FormGroup,
  HStack,
  Input,
  NumberInput,
  OTCSettlementTypeEnum,
  SearchSelect,
  Stack,
  StrikeTypeEnum,
} from '@talos/kyoko';
import { useCallback, useEffect, useMemo } from 'react';
import {
  FORWARD_PAYOFF_TYPE_OPTIONS,
  OPTION_EXERCISE_STYLE_OPTIONS,
  OPTION_STRIKE_TYPE_OPTIONS,
  OTC_SETTLEMENT_TYPE_OPTIONS,
  type ExerciseStyleOption,
} from '../productDefinitions/tokens';
import { TextDividerSimple } from './OTCTextDivider';
import type { OTCFieldsChangeHandler, OTCFieldsContext } from './types';
import {
  useCommonOtcDerivativeProductLayoutLogic,
  type IndexOption,
  type PayoffTypeOption,
  type UsdPeggedOption,
} from './useCommonOtcDerivativeProductLayoutLogic';

interface OptionProductLayoutProps {
  formData: OTCOptionFieldsData;
  disclosureContext: OTCFieldsContext;
  handleFieldChange: OTCFieldsChangeHandler<OTCOptionFieldsData>;
  errors?: Record<string, string>;
  touched?: Record<string, boolean>;
  enableOTCPhysicalSettlement: boolean;
}

export const OptionProductLayout = ({
  formData,
  disclosureContext,
  handleFieldChange,
  errors = {},
  touched = {},
  enableOTCPhysicalSettlement,
}: OptionProductLayoutProps) => {
  const { baseAsset } = disclosureContext.rowData;
  const { enableOTCDeltaStrikeSupport } = useFeatureFlag();

  useEffect(() => {
    if (!enableOTCDeltaStrikeSupport && formData.strikeType === StrikeTypeEnum.Delta) {
      handleFieldChange('strikeType', StrikeTypeEnum.Fixed);
    }
  }, [enableOTCDeltaStrikeSupport, formData.strikeType, handleFieldChange]);

  useEffect(() => {
    if (!enableOTCPhysicalSettlement && formData.settlementType === OTCSettlementTypeEnum.Physical) {
      handleFieldChange('settlementType', OTCSettlementTypeEnum.Cash);
    }
  }, [enableOTCPhysicalSettlement, formData.settlementType, handleFieldChange]);

  const {
    usdPeggedOptions,
    usdPeggedSortFilter,
    availableIndexes,
    selectedIndex,
    payoffDependentData,
    assetSelections,
    payoffTypeSelection,
  } = useCommonOtcDerivativeProductLayoutLogic(formData, disclosureContext);

  const strikeTypeSelection = useMemo(() => {
    if (!isOptionProductFields(formData)) {
      return undefined;
    }
    return OPTION_STRIKE_TYPE_OPTIONS.find(option => option.value === (formData.strikeType ?? StrikeTypeEnum.Fixed));
  }, [formData]);

  const exerciseStyleSelection = useMemo(() => {
    if (!isOptionProductFields(formData)) {
      return undefined;
    }
    return OPTION_EXERCISE_STYLE_OPTIONS.find(option => option.value === (formData.exerciseStyle ?? 'European'));
  }, [formData]);

  const settlementTypeSelection = useMemo(() => {
    if (!isOptionProductFields(formData)) {
      return undefined;
    }
    return OTC_SETTLEMENT_TYPE_OPTIONS.find(option => option.value === formData.settlementType);
  }, [formData]);

  // Payment Ccy defaults to baseAsset for INVERSE, and quoteAsset for LINEAR (non-editable in 2.54)
  const paymentCcy = useMemo(() => {
    if (!isOptionProductFields(formData)) {
      return '';
    }
    if (formData.payoffType === 'Inverse') {
      return baseAsset ?? '';
    } else if (formData.payoffType === 'Linear') {
      return formData.quoteAsset ?? '';
    }
    return '';
  }, [formData, baseAsset]);

  // Strike Price suffix and formatting based on strike type
  const strikeValueSuffix = useMemo(() => {
    if (!isOptionProductFields(formData)) {
      return 'USD';
    }
    if (formData.strikeType === 'Delta') {
      return 'D'; // Display as 10D, 25D, etc.
    }
    return 'USD';
  }, [formData]);

  // Format strike price display
  // For fixed strikes, allow decimals (max 4dp)
  const formattedStrikePrice = useMemo(() => {
    if (!isOptionProductFields(formData) || !formData.strikePrice) {
      return '';
    }
    return formData.strikePrice;
  }, [formData]);

  // Handle blur to validate value for Delta strikes
  // Valid range: 0 < value < 100 (exclusive)
  // Clear invalid values but don't clamp - let validation show the error
  const handleStrikePriceBlur = useCallback(() => {
    if (!isOptionProductFields(formData)) {
      return;
    }
    if (formData.strikeType === 'Delta' && formData.strikePrice) {
      const num = parseFloat(formData.strikePrice);
      if (!isNaN(num)) {
        // Clear if invalid (<= 0 or >= 100) - validation will show error
        if (num <= 0 || num >= 100) {
          handleFieldChange('strikePrice', '');
        }
      }
    }
  }, [formData, handleFieldChange]);

  if (!isOptionProductFields(formData)) {
    return null;
  }

  return (
    <Stack gap="spacing12" w="100%">
      {/* Payoff Type */}
      <FormGroup
        label={getFieldTitle(otcOptionFieldDefinitions.payoffType)}
        tooltip={getFieldTooltip(otcOptionFieldDefinitions.payoffType)}
        error={touched.payoffType ? errors.payoffType : undefined}
        mb={4}
      >
        <SearchSelect<PayoffTypeOption>
          options={FORWARD_PAYOFF_TYPE_OPTIONS}
          selection={payoffTypeSelection}
          onChange={option => handleFieldChange('payoffType', option?.value)}
          getLabel={option => option?.label}
          placeholder="Select Payoff Type"
          showClear
          data-testid="option-payoff-type-select"
        />
      </FormGroup>

      {/* Quote Asset / Delivery Asset */}
      <HStack gap="spacing12">
        <Box flex="1">
          <FormGroup
            label={getFieldTitle(otcOptionFieldDefinitions.quoteAsset)}
            tooltip={getFieldTooltip(otcOptionFieldDefinitions.quoteAsset)}
            error={touched.quoteAsset ? errors.quoteAsset : undefined}
            mb={4}
          >
            <SearchSelect<UsdPeggedOption>
              options={usdPeggedOptions}
              disabled={formData.payoffType === 'Inverse'}
              selection={assetSelections.quoteAssetSelection}
              onChange={option => handleFieldChange('quoteAsset', option?.value)}
              getLabel={option => String(option?.label || '')}
              sortFilterOverride={usdPeggedSortFilter}
              placeholder={
                formData.payoffType === 'Inverse' ? payoffDependentData.quoteAssetPlaceholder : 'Select Quote Asset'
              }
              showClear={formData.payoffType !== 'Inverse'}
              touched={!!touched.quoteAsset}
              invalid={!!errors.quoteAsset}
              data-testid="option-quote-asset-select"
            />
          </FormGroup>
        </Box>
        <Box flex="1">
          <FormGroup
            label={getFieldTitle(otcOptionFieldDefinitions.deliveryAsset)}
            tooltip={getFieldTooltip(otcOptionFieldDefinitions.deliveryAsset)}
            error={touched.deliveryAsset ? errors.deliveryAsset : undefined}
            mb={4}
          >
            <SearchSelect<UsdPeggedOption>
              options={usdPeggedOptions}
              disabled={formData.payoffType === 'Inverse'}
              selection={assetSelections.deliveryAssetSelection}
              onChange={option => handleFieldChange('deliveryAsset', option?.value)}
              getLabel={option => String(option?.label || '')}
              placeholder={payoffDependentData.deliveryAssetPlaceholder}
              sortFilterOverride={usdPeggedSortFilter}
              showClear={formData.payoffType !== 'Inverse'}
              touched={!!touched.deliveryAsset}
              invalid={!!errors.deliveryAsset}
              data-testid="option-delivery-asset-select"
            />
          </FormGroup>
        </Box>
      </HStack>

      {/* Payment Ccy - Non-editable, computed based on payoff type */}
      <FormGroup
        label={getFieldTitle(otcOptionFieldDefinitions.paymentCcy)}
        tooltip={getFieldTooltip(otcOptionFieldDefinitions.paymentCcy)}
        error={touched.paymentCcy ? errors.paymentCcy : undefined}
        mb={4}
      >
        <Input
          type="text"
          disabled={true}
          value={paymentCcy}
          onChange={() => {}}
          data-testid="option-payment-ccy-input"
        />
      </FormGroup>

      {/* Unit Notional */}
      <HStack gap="spacing12">
        <FormGroup
          flex="1"
          label={getFieldTitle(otcOptionFieldDefinitions.unitNotional)}
          tooltip={getFieldTooltip(otcOptionFieldDefinitions.unitNotional)}
          error={touched.unitNotionalAmount ? errors.unitNotionalAmount : undefined}
          mb={4}
        >
          <NumberInput
            id="option-unit-notional-amount"
            autoComplete="off"
            min="0"
            value={formData.unitNotionalAmount?.toString() || ''}
            onChange={value => handleFieldChange('unitNotionalAmount', value)}
            suffix={payoffDependentData.unitNotionalCurrency}
            data-testid="option-unit-notional-input"
            touched={!!touched.unitNotionalAmount}
            invalid={!!errors.unitNotionalAmount}
          />
        </FormGroup>
      </HStack>

      {/* Tenor */}
      <FormGroup
        label={getFieldTitle(otcOptionFieldDefinitions.tenor)}
        tooltip={getFieldTooltip(otcOptionFieldDefinitions.tenor)}
        mb={4}
      >
        <DurationInput
          initialValue={formData.tenor}
          onChange={value => handleFieldChange('tenor', value)}
          dataTestId="option-tenor-input"
          placeholder="Enter custom tenor"
        />
      </FormGroup>

      <TextDividerSimple text="Strike" />

      {/* Strike Section */}
      <HStack gap="spacing12" alignItems="flex-start">
        <Box flex="1">
          <FormGroup
            label={getFieldTitle(otcOptionFieldDefinitions.strikeType)}
            tooltip={getFieldTooltip(otcOptionFieldDefinitions.strikeType)}
            error={touched.strikeType ? errors.strikeType : undefined}
            mb={4}
          >
            <SearchSelect<(typeof OPTION_STRIKE_TYPE_OPTIONS)[number]>
              options={OPTION_STRIKE_TYPE_OPTIONS}
              selection={strikeTypeSelection}
              onChange={option => {
                const newStrikeType = option?.value;
                handleFieldChange('strikeType', newStrikeType);
                // Clear strike price if switching to Delta and current value is invalid for Delta
                if (newStrikeType === StrikeTypeEnum.Delta && formData.strikePrice) {
                  const num = parseFloat(formData.strikePrice);
                  if (!isNaN(num) && (num <= 0 || num >= 100)) {
                    handleFieldChange('strikePrice', '');
                  }
                }
              }}
              getLabel={option => option?.label}
              getDescription={option =>
                !enableOTCDeltaStrikeSupport && option?.value === StrikeTypeEnum.Delta ? '(coming soon)' : undefined
              }
              placeholder="Fixed"
              isItemDisabled={option => !enableOTCDeltaStrikeSupport && option?.value === StrikeTypeEnum.Delta}
              dataTestID="option-strike-type-select"
            />
          </FormGroup>
        </Box>
        <Box flex="1">
          <FormGroup
            label={
              formData.strikeType === 'Delta'
                ? getFieldTitle(otcOptionFieldDefinitions.deltaValue)
                : getFieldTitle(otcOptionFieldDefinitions.strikePrice)
            }
            tooltip={
              formData.strikeType === 'Delta'
                ? getFieldTooltip(otcOptionFieldDefinitions.deltaValue)
                : getFieldTooltip(otcOptionFieldDefinitions.strikePrice)
            }
            error={touched.strikePrice ? errors.strikePrice : undefined}
            mb={4}
          >
            <NumberInput
              value={formattedStrikePrice}
              onChange={value => handleFieldChange('strikePrice', value)}
              onBlur={handleStrikePriceBlur}
              suffix={strikeValueSuffix}
              min="0"
              data-testid="option-strike-price-input"
            />
          </FormGroup>
        </Box>
      </HStack>

      <TextDividerSimple text="Exercise" />

      {/* Exercise Section */}
      <FormGroup
        label={getFieldTitle(otcOptionFieldDefinitions.exerciseStyle)}
        tooltip={getFieldTooltip(otcOptionFieldDefinitions.exerciseStyle)}
        error={touched.exerciseStyle ? errors.exerciseStyle : undefined}
        mb={4}
      >
        <SearchSelect<ExerciseStyleOption>
          options={OPTION_EXERCISE_STYLE_OPTIONS}
          selection={exerciseStyleSelection}
          onChange={option => handleFieldChange('exerciseStyle', option?.value)}
          getLabel={option => option?.label}
          placeholder="European"
          disabled={true}
          data-testid="option-exercise-style-select"
        />
      </FormGroup>

      <TextDividerSimple text="Settlement" />

      {/* Settlement Section */}
      <HStack gap="spacing12" alignItems="flex-start">
        <Box flex="1">
          <FormGroup
            label={getFieldTitle(otcOptionFieldDefinitions.settlementType)}
            tooltip={appendTooltipWhenFeatureDisabled(
              getFieldTooltip(otcOptionFieldDefinitions.settlementType),
              enableOTCPhysicalSettlement,
              PHYSICAL_SETTLEMENT_SUPPORT_UPCOMING
            )}
            error={touched.settlementType ? errors.settlementType : undefined}
            mb={4}
          >
            <SearchSelect<(typeof OTC_SETTLEMENT_TYPE_OPTIONS)[number]>
              options={OTC_SETTLEMENT_TYPE_OPTIONS}
              selection={settlementTypeSelection}
              onChange={option => handleFieldChange('settlementType', option?.value)}
              getLabel={option => option?.label}
              placeholder="Cash"
              data-testid="option-settlement-type-select"
              disabled={!enableOTCPhysicalSettlement}
            />
          </FormGroup>
        </Box>
        <Box flex="1">
          <FormGroup
            label={getFieldTitle(otcOptionFieldDefinitions.index)}
            tooltip={getFieldTooltip(otcOptionFieldDefinitions.index)}
            error={touched.index ? errors.index : undefined}
            mb={4}
          >
            <SearchSelect<IndexOption>
              options={availableIndexes}
              selection={selectedIndex}
              onChange={option => handleFieldChange('index', option?.value || '')}
              getLabel={option => option?.label}
              getDescription={option => option?.description ?? ''}
              placeholder="Select Index"
              showClear={false}
              disabled={!disclosureContext.rowData.baseAsset || !formData.quoteAsset}
              touched={!!touched.index}
              invalid={!!errors.index}
              data-testid="option-index-input"
            />
          </FormGroup>
        </Box>
      </HStack>
    </Stack>
  );
};
